import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import AddCategory from "../Category/AddCategory";
import {
  getAllCategorys,
  deleteCategory,
  createCategory,
  updateCategory,
} from "../../service/categoryService";
import { useSnackbar } from "../../App";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import Color from "../../components/Color";

const Categories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [searchCategory, setSearchCategory] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortModel, setSortModel] = useState([{ field: "id", sort: "desc" }]);
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({ id: null, name: "", description: "" });
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;
      const sortField = sortModel[0]?.field || "id";
      const sortOrder = sortModel[0]?.sort || "desc";

      const res = await getAllCategorys(
        page,
        limit,
        debouncedSearch || "",
        sortField,
        sortOrder
      );

      const data = res?.data?.data;
      if (Array.isArray(data?.data)) {
        setRows(data.data);
        setRowCount(data.total);
      } else {
        setRows([]);
        showSnackbar("Unexpected data format!", "error");
      }
    } catch (error) {
      showSnackbar("Failed to fetch categories!", "error");
    }
    setLoading(false);
  }, [paginationModel, debouncedSearch, sortModel, showSnackbar]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedSearch(searchCategory);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchCategory]);

  const handleDeleteClick = (id) => {
    if (!id) {
      showSnackbar("No category selected!", "error");
      return;
    }
    setDeleteId(id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteCategory(deleteId);
      showSnackbar("Category deleted successfully!", "success");
      fetchCategories();
    } catch (error) {
      const errorMessage = error?.response?.data?.message;

      if (errorMessage === "Cannot delete category with associated products") {
      // Show specific message from backend
      showSnackbar(errorMessage, "warning");
    } else {
      // Generic fallback error
      showSnackbar("Failed to delete category!", "error");
    }
     
    } finally {
      setDeleteDialogOpen(false);
      setDeleteId(null);
      setSelectedRow(null);
    }
  };

  const handleSubmit = async (errorsCallback) => {
    try {
      const payload = { name: formData.name, description: formData.description };
      if (editingId) {
        await updateCategory(editingId, payload);
        showSnackbar("Category updated successfully!", "success");
      } else {
        await createCategory(payload);
        showSnackbar("Category added successfully!", "success");
      }
      setOpenCategoryModal(false);
      setEditingId(null);
      setFormData({ id: null, name: "", description: "" });
      fetchCategories();
    } catch (error) {
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        errorsCallback(apiErrors); // Pass errors to AddCategory
        const errorMessage =
          apiErrors.name?.[0] === "The name has already been taken."
            ? "The category name is already taken. Please use a different name."
            : "Validation failed. Please check the form.";
        showSnackbar(errorMessage, "error");
      } else {
        showSnackbar(error?.response?.data?.message || "Something went wrong!", "error");
      }
    }
  };

  const handleEdit = (category) => {
    setFormData({
      id: category.id,
      name: category.name || "",
      description: category.description || "",
    });
    setEditingId(category.id);
    setOpenCategoryModal(true);
    handleMenuClose();
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      minWidth: isMobile ? 80 : 150,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row)}
          sx={{
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: 1.5,
            padding: isMobile ? "10px" : "8px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.08)",
            },
          }}
        >
          <MoreVertIcon sx={{ fontSize: isMobile ? 18 : 16 }} />
        </IconButton>
      ),
    },
    { field: "name", headerName: "Category Name", flex: 1, minWidth: isMobile ? 120 : 200 },
    { field: "description", headerName: "Description", flex: 2, minWidth: 200 },
  ];

  return (
    <Box sx={{ padding: isMobile ? 1 : 0, mb: { xs: 0 } }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mt={2}
      >
        <TextField
          value={searchCategory}
          onChange={(e) => setSearchCategory(e.target.value)}
          placeholder="Search category name"
          variant="outlined"
          InputLabelProps={{ shrink: false }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: isMobile ? 18 : 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchCategory && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchCategory("");
                    setDebouncedSearch("");
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                >
                  ✖
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputLabel-root": { display: "none" },
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
              height: isMobile ? "34px" : "37.69px",
              fontSize: isMobile ? "0.85rem" : "1rem",
            },
            width: isMobile ? "100%" : 250,
            mb: isMobile ? 1 : 0,
          }}
        />
        <Button
          sx={{
            background: Color.primary.main,
            color: "white",
            height: isMobile ? "34px" : "37.69px",
            width: isMobile ? "100%" : 180,
            fontSize: isMobile ? "0.85rem" : "1rem",
            mt: isMobile ? 0 : 0,
            "&:hover": {
              background: Color.primary.dark,
            },
          }}
          onClick={() => {
            setEditingId(null);
            setFormData({ id: null, name: "", description: "" });
            setOpenCategoryModal(true);
          }}
          startIcon={<AddIcon />}
        >
          Add Category
        </Button>
      </Box>

      <Box sx={{ width: "100%", mt: isMobile ? 2 : 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          rowHeight={isMobile ? 48 : 56}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          autoHeight
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "14px" : "18px",
              fontWeight: 500,
              pl: 1,
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "14px",
              pl: 2,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f4f4f4",
            },
            "& .MuiLinearProgress-bar": {
              backgroundColor: Color.primary.main,
            },
            "& .MuiLinearProgress-colorPrimary": {
              backgroundColor: Color.secondary.main,
            },
            overflowX: isMobile ? "hidden" : "auto",
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: isMobile ? "90vw" : 200,
          },
        }}
      >
        <MenuItem
          onClick={() => handleEdit(selectedRow)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleDeleteClick(selectedRow?.id)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} /> Delete
        </MenuItem>
      </Menu>

      <AddCategory
        open={openCategoryModal}
        onClose={() => {
          setOpenCategoryModal(false);
          setEditingId(null);
          setFormData({ id: null, name: "", description: "" });
        }}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : "auto",
            m: isMobile ? 1 : 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
          Delete Confirmation
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Are you sure you want to delete the category <strong>{selectedRow?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            mr: 2,
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.label.main,
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
              "&:hover": {
                background: Color.primary.dark,
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;
